import React, { ReactNode, useState } from 'react';
import DelayReasonModal from '@/components/pages/kiosk/tablet/v2/DelayReasonModal';
import { useToaster } from '@/components/core/Toaster';
import {
  PREOP_DELAY_POLL_MS,
  useProcedureDelayReason,
} from '@/components/entities/patient/procedureDelayReason/useProcedureDelayReason';
import {
  DelayReasonPatientRef,
  resolvePatientProcedure,
} from '@/components/entities/patient/procedureDelayReason/procedureDelay';

/** Kiosk patients come through `mapPatientData`, which maps `physician` to a name string. */
type PreOpPatient = DelayReasonPatientRef & {
  physician?: string | { name?: string | null } | null;
};

type ButtonRenderProps = {
  disabled: boolean;
  onClick: () => void;
  reasonLabel?: string;
};

type PreOpDelayReasonControlProps = {
  patient?: PreOpPatient | null;
  renderButton: (props: ButtonRenderProps) => ReactNode;
};

const physicianNameOf = (physician: PreOpPatient['physician']): string | undefined => {
  if (!physician) {
    return undefined;
  }
  if (typeof physician === 'string') {
    return physician;
  }
  return physician.name ?? undefined;
};

const PreOpDelayReasonControl = ({ patient, renderButton }: PreOpDelayReasonControlProps) => {
  const [open, setOpen] = useState(false);
  const toaster = useToaster();
  const procedure = resolvePatientProcedure(patient);
  const {
    ready,
    isFirstCaseOfDay,
    isDelayed,
    existingLoading,
    reasonLabel,
    existingReasonId,
    existingOtherDescription,
    configuredDelayReasons,
    delayInfo,
    delayDurationSeconds,
    saveReason,
    saving,
  } = useProcedureDelayReason({
    procedureId: procedure?.id,
    patientId: patient?.id,
    operationRoomId: procedure?.orId,
    startTime: procedure?.startTime,
    pollIntervalMs: PREOP_DELAY_POLL_MS,
  });

  // Only the first case of the day in its OR can have an OR delay reason, so
  // other patients do not get the button at all. The first case sees it
  // disabled until the FCOTS grace period has passed.
  if (!ready || !isFirstCaseOfDay) {
    return null;
  }

  const disabled = !isDelayed || existingLoading;

  return (
    <>
      {renderButton({
        disabled,
        reasonLabel,
        onClick: () => {
          if (!disabled) {
            setOpen(true);
          }
        },
      })}
      {open && (
        <DelayReasonModal
          dismissible
          submitting={saving}
          onDismiss={() => setOpen(false)}
          onSkip={() => setOpen(false)}
          onSubmit={async (reason, otherDescription) => {
            try {
              await saveReason(reason, otherDescription);
              setOpen(false);
            } catch (error) {
              console.error('Failed to save delay reason', error);
              toaster?.error('Failed to save delay reason. Please try again.');
            }
          }}
          physicianName={delayInfo?.physician || physicianNameOf(patient?.physician)}
          patientName={delayInfo?.patientName || patient?.name || undefined}
          gracePeriodMinutes={delayInfo?.gracePeriodMinutes ?? 5}
          delayDurationSeconds={delayDurationSeconds}
          delayReasons={configuredDelayReasons}
          existingReason={existingReasonId}
          existingOtherDescription={existingOtherDescription}
        />
      )}
    </>
  );
};

export default PreOpDelayReasonControl;
